import * as React from 'react';
import { FieldRenderProps } from 'react-final-form';
import { Field } from '../form/Field';
import { VFFilterFieldProps } from '../form/types';
import { StringInput } from '../string/StringInput';
import { FilterItem } from './FilterItem';
import { FilterFieldProps, getFilterFieldFormValuePath } from './types';
import { DATA_FILTER_INPUT } from '../common/data';

export type StringProps = VFFilterFieldProps<
  React.ComponentProps<typeof StringInput>
> &
  FilterFieldProps;
export const FilterString = React.forwardRef<HTMLInputElement, StringProps>(
  (
    {
      name,
      validate,
      className,
      inputClassName,
      label,
      onChange: customOnChange,
      disabled,
      size = 14,
      filterType,
      extendedFilter,
      ...other
    },
    ref
  ) => {
    return (
      <FilterItem
        name={name}
        className={className}
        label={label}
        disabled={disabled}
        filterType={filterType}
        extendedFilter={extendedFilter}
      >
        <Field
          name={getFilterFieldFormValuePath(name, filterType)}
          validate={validate}
        >
          {({
            input: { value, onChange, name, ...nother },
            meta,
          }: FieldRenderProps<string>) => {
            const [displayValue, setDisplayValue] = React.useState<
              string | undefined
            >(value || undefined);
            const prevSentValue = React.useRef<string | undefined>(
              value || undefined
            );

            React.useEffect(() => {
              if (value !== prevSentValue.current) {
                prevSentValue.current = value || undefined;
                setDisplayValue(value || undefined);
              }
            }, [value]);

            const handleChange = React.useCallback(
              (e: React.ChangeEvent<HTMLInputElement>) => {
                const untrimmedValue = e.target.value;
                setDisplayValue(untrimmedValue);

                let realVal: string | undefined = untrimmedValue?.trim?.();
                if (realVal === '') {
                  realVal = undefined;
                }
                prevSentValue.current = realVal;
                onChange({ target: { name, value: realVal } });
                customOnChange && customOnChange(realVal as any);
              },
              [customOnChange, onChange, name]
            );

            const handleBlur = React.useCallback(() => {
              const trimmed = displayValue?.trim?.() || undefined;
              setDisplayValue(trimmed);
              prevSentValue.current = trimmed;
              if (trimmed !== (value || undefined)) {
                onChange({ target: { name, value: trimmed } });
              }
            }, [displayValue, value, onChange, name]);
            return (
              <StringInput
                {...DATA_FILTER_INPUT(name)}
                className={inputClassName}
                // @ts-ignore
                ref={ref}
                size={size}
                {...nother}
                name={name}
                {...other}
                disabled={disabled || meta.submitting}
                onChange={handleChange}
                onBlur={handleBlur}
                value={displayValue ?? ''}
              />
            );
          }}
        </Field>
      </FilterItem>
    );
  }
);
